/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {error} from '@ion/core/src/logger/logger';
import {getJob, loadAllJobDevices} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CreateJob, DownloadJobType} from '@ion/core/src/types/entities';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import {useRerunJob} from '@ion/query/src/hooks/useRerunJob';
import {
  cloneJobTasks,
  createClonedJobName,
  getMaxSearchWithIds,
  jobTaskFilesNotFound,
  normalizeJobTasks
} from '../components/jobs/helpers';
import {notifyJobRerun} from '../components/jobs/JobRerunListeners';
import {showJobCreateEditor} from '../components/jobs/state';
import {clearDeviceSelection} from '../pages/devices/state';

/**
 * useJobSearchRerun hook used to rerun job 2.0
 *
 * ```typescript
 *
 * import {useJobSearchRerun} from '@ion/applications/src/hooks/';
 *
 * const rerunJob = useJobSearchRerun();
 * 
 * ```
 */
export const useJobSearchRerun = () => {
  const dispatch                                = useDispatch();
  const [reRunJobError, setReRunJobError]       = useState();
  const [reRunJobSelected, setReRunJobSelected] = useState();

  const {
    isLoading: jobRerunLoading,
    rerunJob: rerunSelectedJob
  } = useRerunJob();

  const cloneJobInfo = async(
    job: CreateJob,
    isRerun: boolean = false,
    isRerunFailed: boolean = false): Promise<CreateJob> => {
    const jobOrig = await getJob(job?.jobId);
    const tasks   = await cloneJobTasks(jobOrig);

    const {
      description,
      account,
      criteria
    } = jobOrig;

    return {
      name           : createClonedJobName(jobOrig, isRerun, isRerunFailed),
      description    : description || null,
      progressStatus : DownloadJobType.Pending,
      account,
      criteria,
      tasks
    };
  };

  const rerunJob = useCallback(async(job: CreateJob) => {
    try {
      const rerunJob = await cloneJobInfo(job, true);
      if (jobTaskFilesNotFound(rerunJob)) {
        dispatch(clearDeviceSelection());
        dispatch(showJobCreateEditor(null, job, true));
      } else {
        const normalizedJob = normalizeJobTasks(rerunJob);
        rerunSelectedJob(normalizedJob, {
          onSuccess : (job) => {
            setReRunJobSelected(job);
            notifyJobRerun(job);
          },
          onError : (err) => {
            setReRunJobError(err);
          }
        });
      }
    } catch (err) {
      setReRunJobError(err);
      error(err);
    }
  }, []);

  const rerunFailed = useCallback(async(job: CreateJob, taskId, isTask = false) => {
    try {
      const rerunJob = await cloneJobInfo(job, true, true);
      if (jobTaskFilesNotFound(rerunJob)) {
        dispatch(clearDeviceSelection());
        dispatch(showJobCreateEditor(null, job, true));
      } else {
        const {account, id, jobId} = job;
        const jobQuery = `(jobId: ${id || jobId}`;
        const taskQuery = isTask ? jobQuery + ` AND taskIndex: ${taskId}`: jobQuery;
        const query = taskQuery + ' AND deviceTaskStatus: FAILED)';
        const payload = {
          accountId        : entityId(account),
          pageId           : 1,
          pageSize         : 100,
          sortOn           : [],
          includeSubGroups : true,
          query            : {query_string: {query}}
        };
        const isParallel      = true;
        const isSequenceAfter = true;
        const jobs = await loadAllJobDevices(payload, isParallel, isSequenceAfter);
        const devices = jobs?.map(job => job?.device)?.filter(device => !!device?.id);
        const selectedDevices = removeDuplicate('id', devices, false);
        const normalizedJob = normalizeJobTasks({
          ...rerunJob,
          criteria : getMaxSearchWithIds(selectedDevices, entityId(account), true)
        }, taskId, isTask);
        rerunSelectedJob(normalizedJob, {
          onSuccess : (job) => {
            setReRunJobSelected(job);
            notifyJobRerun(job);
          },
          onError : (err) => {
            setReRunJobError(err);
          }
        });
      }
    } catch (err) {
      setReRunJobError(err);
      error(err);
    }
  }, []);

  return {
    reRunJobSelected,
    jobRerunLoading,
    reRunJobError,
    rerunFailed,
    rerunJob
  };
};

export default useJobSearchRerun;
